import {
  Badge,
  Button,
  ChapterCard,
  EmptyState,
  GameHud,
  HintCounter,
  LevelCard,
  LivesCounter,
  Skeleton,
  StarRating,
} from "@monekineko/ui-kit";
import { formatClock } from "@monekineko/shared";
import { useSession } from "../state/session.js";
import { haptic } from "../telegram.js";
import styles from "./screens.module.css";

// Карта: главы и уровни. Что открыто, решает сервер — здесь только показ.

const GAME_TYPE_LABEL: Record<string, string> = {
  binary_lock: "двоичный замок",
  robot_repair: "починка роботом",
  circuit_puzzle: "пазл микросхем",
};

export function MapScreen({
  onPlay,
  onOpenFriends,
  onOpenLeaderboard,
}: {
  onPlay: (levelId: string) => void;
  onOpenFriends: () => void;
  onOpenLeaderboard: () => void;
}) {
  const { profile, map, loading } = useSession();

  if (loading || !profile || !map) {
    return (
      <div className={styles.screen}>
        <Skeleton height={56} />
        <Skeleton height={140} />
        <Skeleton height={64} />
        <Skeleton height={64} />
      </div>
    );
  }

  const noContent = map.chapters.length === 0;

  return (
    <div className={styles.screen}>
      <GameHud
        left={
          <>
            <LivesCounter lives={profile.lives} max={profile.livesMax} nextLifeIn={profile.nextLifeIn} />
            <HintCounter hints={profile.hints} />
          </>
        }
        right={
          <span className={styles.points}>
            <StarRating value={Math.min(3, profile.stars)} /> {profile.points}
          </span>
        }
      />

      {noContent ? (
        <EmptyState
          title="Главы ещё готовятся"
          description="Как только их опубликуют в админке, они появятся здесь."
        />
      ) : null}

      {map.chapters.map((chapter) => (
        <section key={chapter.id} className={styles.chapterBlock}>
          <ChapterCard
            title={chapter.title}
            subtitle={chapter.subtitle}
            coverUrl={chapter.coverUrl}
            locked={chapter.status === "locked"}
            lockHint={chapter.unlockHint}
            progress={
              <div className={styles.chapterMeta}>
                <Badge tone={chapter.status === "completed" ? "success" : "neutral"}>
                  {chapter.levels.filter((level) => level.status === "completed").length} из{" "}
                  {chapter.levels.length}
                </Badge>
              </div>
            }
          />
          <div className={styles.levels}>
            {chapter.levels.map((level, index) => (
              <LevelCard
                key={level.id}
                index={index + 1}
                title={level.title}
                status={level.status}
                stars={level.stars}
                meta={
                  level.status === "locked"
                    ? level.unlockHint
                    : level.bestDurationMs
                      ? `${GAME_TYPE_LABEL[level.gameType] ?? level.gameType} · лучшее время ${formatClock(level.bestDurationMs)}`
                      : GAME_TYPE_LABEL[level.gameType] ?? level.gameType
                }
                onClick={() => {
                  haptic("medium");
                  onPlay(level.id);
                }}
              />
            ))}
          </div>
        </section>
      ))}

      <div className={styles.bottomActions}>
        <Button variant="secondary" block onClick={onOpenFriends}>
          Друзья · {profile.invitedCount}
        </Button>
        <Button variant="secondary" block onClick={onOpenLeaderboard}>
          Рейтинг
        </Button>
      </div>
    </div>
  );
}
